import { useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { get } from "../api.js";
import { ManageLessonRow } from "../booking/ManageLessonRow.js";
import { formatDateTime } from "../format.js";
import { useAsync } from "../useAsync.js";
import { LESSON_WEEK_LABEL } from "@wmf/shared/lessonCalendar";
import type { AdminFamilyOverviewData } from "./AdminFamilyOverview.js";
import { upcomingLessonWeek } from "./upcomingLessonWeek.js";

export interface OverviewUpcomingLesson {
  id: string;
  studentName: string;
  startsAt: string;
  endsAt: string;
  serviceName: string | null;
  tutorName?: string | null;
  status: string;
  school?: boolean;
  href?: string;
  ameliaAppointmentId?: number | null;
}

interface Props {
  studentId?: string;
  lessons: OverviewUpcomingLesson[];
  familyEnabled?: boolean;
  familyHref?: string;
  currentRecordHref?: string;
  canManage?: boolean;
  refreshRevision?: number;
  onChanged: () => void;
}

export function StudentUpcomingLessons(props: Props) {
  return props.familyEnabled && props.studentId
    ? <FamilyUpcomingLessons key={props.studentId} {...props} studentId={props.studentId} />
    : <UpcomingLessons lessons={upcomingLessonWeek(props.lessons)} canManage={props.canManage} onChanged={props.onChanged} />;
}

function FamilyUpcomingLessons(props: Props & { studentId: string }) {
  const [parentUserId, setParentUserId] = useState<string | null>(null);
  const accountId = useId();
  const family = useAsync(() => get<AdminFamilyOverviewData>(
    `/admin/students/${encodeURIComponent(props.studentId)}/family-overview`,
    { parentUserId: parentUserId ?? undefined, limit: "1" },
  ), [props.studentId, parentUserId, props.refreshRevision], { reloadOnMirrorRefresh: true });
  // Keep the same household mounted during refresh; another account/student never reuses it.
  const data = family.data?.anchor?.id === props.studentId &&
    (parentUserId === null || family.data.account?.userId === parentUserId) ? family.data : null;
  const accounts = family.data?.anchor?.id === props.studentId ? family.data.accounts : [];
  const hasFamily = !!data?.account && Array.isArray(data.nextWeekLessons);
  const unavailable = !!family.error || (!!data?.account && !Array.isArray(data.nextWeekLessons));
  const refreshing = family.loading || unavailable;
  const lessons: OverviewUpcomingLesson[] = hasFamily && data.nextWeekLessons
    ? data.nextWeekLessons.map(lesson => ({
      id: `${lesson.id}:${lesson.studentId}`,
      studentName: data.members.find(member => member.id === lesson.studentId)?.fullName ?? "Student",
      startsAt: lesson.startsAt, endsAt: lesson.endsAt, serviceName: lesson.serviceName,
      tutorName: lesson.tutorName, status: lesson.status === "pending" || lesson.appointmentStatus === "pending" ? "pending" : lesson.status,
      school: lesson.domain === "school",
      href: lesson.recordHref === (props.currentRecordHref ?? `/students/${encodeURIComponent(props.studentId)}`) ? undefined : lesson.recordHref,
      // Existing row controls apply only to the opened record. Siblings open their own record.
      ameliaAppointmentId: lesson.studentId === props.studentId && lesson.domain === "private" ? lesson.ameliaAppointmentId : null,
    })) : upcomingLessonWeek(props.lessons);
  return <UpcomingLessons lessons={lessons} family={hasFamily} loadingFamily={family.loading} refreshing={refreshing} canManage={props.canManage} onChanged={props.onChanged}>
    {accounts.length > 1 && <div className="student-record-family-choice">
      <label htmlFor={accountId}>Parent account</label>
      <select id={accountId} aria-disabled={refreshing} value={parentUserId ?? data?.account?.userId ?? ""}
        onChange={event => { if (!refreshing) setParentUserId(event.target.value); }}>
        {accounts.map(account => <option key={account.userId} value={account.userId}>{account.email ?? account.username ?? "Parent account"}</option>)}
      </select>
    </div>}
    {hasFamily && data.accounts.length === 1 && <p className="small muted">Parent account: {data.account?.email ?? data.account?.username ?? "Linked parent"}</p>}
    {family.loading && <p className="small muted" role="status">{hasFamily ? "Refreshing family lessons…" : "Loading family lessons…"}</p>}
    {unavailable && <p className="small error" role="alert">
      {hasFamily ? "We could not refresh the family lessons. Showing the last checked appointments; lesson controls are paused." : "Family lessons are unavailable. Showing this student’s lessons."} <button type="button" className="quiet" onClick={family.reload}>Retry family lessons</button>
    </p>}
    {hasFamily && props.familyHref && <Link className="student-record-family-shortcut" to={props.familyHref}>View all family lessons</Link>}
  </UpcomingLessons>;
}

function UpcomingLessons({ lessons, family, loadingFamily, refreshing, canManage, onChanged, children }: {
  lessons: OverviewUpcomingLesson[]; family?: boolean; loadingFamily?: boolean; refreshing?: boolean; canManage?: boolean; onChanged: () => void; children?: ReactNode;
}) {
  const headingId = useId();
  return <section className="student-record-next" aria-labelledby={headingId} aria-busy={loadingFamily}>
    <div className="student-record-next-heading"><div>
      <span className="small muted">{family ? "Next family lessons" : "Next lessons"}</span>
      <h2 id={headingId}>{lessons.length ? "Coming up" : loadingFamily ? "Checking family lessons" : "No upcoming lessons"}</h2>
      {lessons.length > 0 && <p className="small muted student-record-week-note">All upcoming lessons in the next booked week · {LESSON_WEEK_LABEL}</p>}
    </div></div>
    {children}
    {lessons.length === 0 ? !loadingFamily && <p className="student-record-next-empty">Nothing is booked ahead{family ? " for this family" : " for this student"}.</p>
      : <ul className="student-record-next-lessons" aria-label={family ? "Upcoming family lessons this week" : "Upcoming lessons this week"}>
        {lessons.map(lesson => {
          const minutes = Math.round((Date.parse(lesson.endsAt) - Date.parse(lesson.startsAt)) / 60000);
          return <li key={lesson.id}>
            <div className="student-record-lesson-person"><strong>{lesson.studentName}</strong>
              {lesson.status === "pending" && <span className="tag warn">Awaiting confirmation</span>}</div>
            {lesson.ameliaAppointmentId ? <ManageLessonRow appointmentId={lesson.ameliaAppointmentId}
              canManage={canManage === true && !refreshing} studentName={lesson.studentName} startsAt={lesson.startsAt}
              serviceName={lesson.serviceName} onChanged={onChanged}>{null}</ManageLessonRow>
              : <time dateTime={lesson.startsAt}>{formatDateTime(lesson.startsAt)}</time>}
            <div className="small muted student-record-next-meta">
              <span>{lesson.serviceName ?? "Lesson"}{minutes > 0 ? ` · ${minutes} min` : ""}
                {lesson.tutorName ? ` · ${lesson.tutorName}` : ""}{lesson.school ? " · School" : ""}</span>
            </div>
            {lesson.href && !(lesson.ameliaAppointmentId && canManage) && <Link className="small student-record-lesson-link" to={lesson.href}
              aria-label={family ? `Open ${lesson.studentName}’s student record` : `Open ${lesson.serviceName ?? "lesson"} for ${lesson.studentName}`}>
              {family ? "Open student record" : "Open lesson"}
            </Link>}
          </li>;
        })}
      </ul>}
  </section>;
}
